import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Coins, Package, TrendingUp } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { riderApi, type EarningsSummary } from "@/features/delivery/api";

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

function Card({
    label,
    value,
    hint,
    icon: Icon,
    tone,
    testId,
}: {
    label: string;
    value: string;
    hint?: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: string;
    testId: string;
}) {
    return (
        <div data-testid={testId} className="rounded-2xl border border-border/70 bg-card p-4 shadow-soft">
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{label}</span>
                <span className={`grid h-8 w-8 place-items-center rounded-lg ${tone}`}>
                    <Icon className="h-4 w-4" />
                </span>
            </div>
            <p className="mt-3 text-2xl font-extrabold">{value}</p>
            {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
        </div>
    );
}

export default function RiderEarnings() {
    const { data, isLoading } = useQuery<EarningsSummary>({
        queryKey: ["rider", "earnings"],
        queryFn: riderApi.earnings,
        refetchInterval: 30000,
    });

    if (isLoading || !data) {
        return <p className="text-sm text-muted-foreground">Crunching your numbers…</p>;
    }

    return (
        <div className="space-y-4" data-testid="rider-earnings-page">
            <div>
                <h1 className="text-xl font-extrabold">Earnings</h1>
                <p className="text-xs text-muted-foreground">Payments settle to your bank each Tuesday.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Card
                    label="Today"
                    value={rupee(data.today.earnings)}
                    hint={`${data.today.deliveries} deliveries`}
                    icon={CalendarDays}
                    tone="bg-brand/10 text-brand"
                    testId="rider-earnings-today"
                />
                <Card
                    label="This week"
                    value={rupee(data.weekly.earnings)}
                    hint={`${data.weekly.deliveries} deliveries`}
                    icon={TrendingUp}
                    tone="bg-emerald-500/10 text-emerald-600"
                    testId="rider-earnings-week"
                />
                <Card
                    label="This month"
                    value={rupee(data.monthly.earnings)}
                    hint={`${data.monthly.deliveries} deliveries`}
                    icon={Package}
                    tone="bg-amber-500/10 text-amber-600"
                    testId="rider-earnings-month"
                />
                <Card
                    label="Lifetime"
                    value={rupee(data.total.earnings)}
                    hint={`${data.total.deliveries} deliveries`}
                    icon={Coins}
                    tone="bg-violet-500/10 text-violet-600"
                    testId="rider-earnings-total"
                />
            </div>

            <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-soft">
                <h3 className="font-bold">Last 30 days</h3>
                <div className="mt-2 h-48">
                    <ResponsiveContainer width="100%" height={192}>
                        <AreaChart data={data.series}>
                            <defs>
                                <linearGradient id="e" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="date" tickFormatter={(v: string) => v.slice(5)} fontSize={10} />
                            <YAxis tickFormatter={(v: number) => `₹${v}`} fontSize={10} />
                            <Tooltip formatter={(v) => rupee(Number(v ?? 0))} />
                            <Area
                                type="monotone"
                                dataKey="earnings"
                                stroke="#10b981"
                                fill="url(#e)"
                                strokeWidth={2}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
